// @flow
import { select, all, call, fork, put, takeEvery } from 'redux-saga/effects';

import {
    translationSaved,
    translationLoaded,
    translationDeleted,
    translationRequestFailed,
    translationSavedAndQuit,
    translationRestored,
    translationChoiceReset,
    appRedirect,
    appMessageThrow
} from '../actions';
import {fetchJSON} from "../../helpers/api";

const JSON_HEADERS = { 'Content-Type': 'application/json' };

/**
 * Common error handling for the write requests of the page
 * @param {*} error
 */
function* handleWriteError(error: any): any {
    console.warn(error);
    if (error.status_code === 404) {
        yield put(appRedirect('/error-404'));
    } else if (error.status_code === 422 || error.status_code === 409) {
        yield put(appMessageThrow(error.message, 'warning'));
        yield put(translationRequestFailed());
    } else {
        yield put(appRedirect('/error-500'));
    }
}

/**
 * Fetch the detail of a text: every version of base / ai / manual
 * @param {number} domain_id
 * @param {number} id
 */
function* fetchDetail(domain_id: number, id: number): any {
    return yield call(fetchJSON, '/domains/'+domain_id+'/translations/'+id, {
        method: 'GET',
        headers: JSON_HEADERS,
    });
}

/**
 * Watch load translation request
 * @returns {IterableIterator<ForkEffect>}
 */
export function* watchTranslationLoad(): any {
    yield takeEvery('TRANSLATION_LOAD', function*({ payload: { domain_id, id } }) {
        try {
            const response = yield call(fetchDetail, domain_id, id);
            yield put(translationLoaded(response));
        } catch (error) {
            console.warn(error);
            if (error.status_code === 404) {
                yield put(appRedirect('/error-404'));
            } else {
                yield put(appRedirect('/error-500'));
            }
        }
    });
}

/**
 * Watch user want to save a translation.
 *
 * Two independent things may have to be sent:
 *  - the text of the manual translation (POST the first time, PUT afterwards),
 *  - the chosen level, as `{level, active_id: null}` ("this level, newest version").
 *    A pinned version is only ever written by "Restore this version".
 * @returns {IterableIterator<ForkEffect>}
 */
export function* watchTranslationSave(): any {
    yield takeEvery('TRANSLATION_SAVE', function*({ payload: { domain_id, quit } }) {
        try {
            const state = yield select();
            const form = state.Translation;
            const detail = form.translation;
            const manual = detail.translations.find((version) => version.type === 'manual');
            const url = '/domains/'+domain_id+'/translations';

            // Only send the text when there is something new to say
            const manualChanged = form.manualEdited
                && form.manualText !== ''
                && (!manual || manual.text !== form.manualText);

            if (manualChanged) {
                const options = {
                    body: JSON.stringify({translation: form.manualText}),
                    method: 'POST',
                    headers: JSON_HEADERS,
                };

                let textUrl = url+'/'+detail.id;
                if (manual) {
                    options.method = 'PUT';
                    // The id of a manual entry is a revisions.id
                    textUrl += '/'+manual.id;
                }

                yield call(fetchJSON, textUrl, options);
            }

            // Record the choice. The manual level cannot be chosen when there is no manual text at all
            const canChoose = form.selectedLevel !== 'manual' || manual || manualChanged;
            // Compare with the last recorded level (`selected`), not with `active`: the latter is the
            // level SERVED, which differs from the recorded choice while the AI policy overrides it
            if (form.selectedLevel && form.selectedLevel !== form.savedLevel && canChoose) {
                yield call(fetchJSON, url+'/override', {
                    body: JSON.stringify({
                        hash: detail.hash,
                        language: detail.target_language,
                        level: form.selectedLevel,
                        active_id: null,
                    }),
                    method: 'PUT',
                    headers: JSON_HEADERS,
                });
            }

            const response = yield call(fetchDetail, domain_id, detail.id);
            if (quit) {
                yield put(translationSavedAndQuit(response));
            } else {
                yield put(translationSaved(response));
            }
        } catch (error) {
            yield call(handleWriteError, error);
        }
    });
}

/**
 * Watch user want to restore one exact version ("Restore this version")
 * @returns {IterableIterator<ForkEffect>}
 */
export function* watchTranslationRestore(): any {
    yield takeEvery('TRANSLATION_RESTORE', function*({ payload: { domain_id, level, active_id } }) {
        try {
            const state = yield select();
            const detail = state.Translation.translation;

            try {
                yield call(fetchJSON, '/domains/'+domain_id+'/translations/override', {
                    body: JSON.stringify({
                        hash: detail.hash,
                        language: detail.target_language,
                        level,
                        active_id,
                    }),
                    method: 'PUT',
                    headers: JSON_HEADERS,
                });
            } catch (error) {
                // A 400 means the version is not alive anymore (e.g. deleted since render):
                // tell the user and refresh the list of versions
                if (error.status_code !== 400) {
                    throw error;
                }
                yield put(appMessageThrow(error.message, 'warning'));
            }

            const response = yield call(fetchDetail, domain_id, detail.id);
            yield put(translationRestored(response));
        } catch (error) {
            yield call(handleWriteError, error);
        }
    });
}

/**
 * Watch user want to go back to the default priority.
 *
 * Clears the recorded choice of this text (`DELETE /translations/override`). Sending
 * `active_id: null` would not do it: that is a real choice meaning "this level, newest".
 * `fetchJSON` only adds the JSON content type for POST/PUT, so the key travels in the query.
 * @returns {IterableIterator<ForkEffect>}
 */
export function* watchTranslationResetChoice(): any {
    yield takeEvery('TRANSLATION_RESET_CHOICE', function*({ payload: { domain_id } }) {
        try {
            const state = yield select();
            const detail = state.Translation.translation;

            yield call(fetchJSON, '/domains/'+domain_id+'/translations/override', {
                search: {hash: detail.hash, language: detail.target_language},
                method: 'DELETE',
                headers: JSON_HEADERS,
            });

            const response = yield call(fetchDetail, domain_id, detail.id);
            yield put(translationChoiceReset(response));
        } catch (error) {
            yield call(handleWriteError, error);
        }
    });
}

/**
 * Watch user want to delete the manual translation
 * @returns {IterableIterator<ForkEffect>}
 */
export function* watchTranslationDelete(): any {
    yield takeEvery('TRANSLATION_DELETE', function*({ payload: { domain_id, id } }) {
        try {
            const state = yield select();
            const detail = state.Translation.translation;
            const manual = detail.translations.find((version) => version.type === 'manual');

            const options = {
                method: 'DELETE',
                headers: JSON_HEADERS,
            };

            // DELETE route needs (domain_id, translationId, revisionId)
            const url = '/domains/'+domain_id+'/translations/'+detail.id + '/'+manual.id;

            yield call(fetchJSON, url, options);
            yield put(translationDeleted(id));
        } catch (error) {
            yield call(handleWriteError, error);
        }
    });
}

function* TranslationSaga(): any {
    yield all([
        fork(watchTranslationSave),
        fork(watchTranslationLoad),
        fork(watchTranslationRestore),
        fork(watchTranslationResetChoice),
        fork(watchTranslationDelete),
    ]);
}

export default TranslationSaga;
